import { getEscoltas, createEscolta, updateEscolta, deleteEscolta } from '../models/escoltaModel.js';
import Swal from 'sweetalert2';
import pb from '../lib/pocketbase.js';
import { toasts } from "svelte-toasts";

/**
 * Fetch escoltas from the database with pagination.
 * @param {number} page - The page number.
 * @param {number} perPage - The number of items per page.
 * @returns {Object} - An object containing the escoltas, total records, and current page.
 */
export async function fetchEscoltas(page, perPage) {
  try {
    const response = await getEscoltas(page, perPage);
    return {
      escolta: response.items,
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    console.error("Error fetching escoltas:", error);
    Swal.fire('Error', 'No se pudieron cargar las escoltas', 'error');
    return null;
  }
}

/**
 * Save or update an escolta in the database.
 * @param {Object} escolta - The escolta object to save or update.
 * @param {string} currentEscoltaId - The ID of the escolta to update (if applicable).
 */
export async function saveEscolta(escolta, currentEscoltaId) {
  try {
    if (currentEscoltaId) {
      await updateEscolta(currentEscoltaId, escolta);
      toasts.add({
        title: 'Éxito',
        description: 'Escolta actualizada exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true
      });
    } else {
      await createEscolta(escolta);
      toasts.add({
        title: 'Éxito',
        description: 'Escolta creada exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true
      });
    }
  } catch (error) {
    console.error("Error saving escolta:", error);
    toasts.add({
      title: 'Error',
      description: 'Ocurrió un error al guardar la escolta',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true
    });
  }
}

/**
 * Remove an escolta from the database.
 * @param {string} id - The ID of the escolta to delete.
 */
export async function removeEscolta(id) {
  try {
    await deleteEscolta(id);
    toasts.add({
      title: 'Éxito',
      description: 'Escolta eliminada exitosamente.',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true
    });
  } catch (error) {
    console.error("Error deleting escolta:", error);
    Swal.fire('Error', 'No se pudo eliminar la escolta', 'error');
  }
}

/**
 * Subscribe to real-time changes in the 'escolta' collection.
 * @param {string} searchQuery - The search query (if applicable).
 * @param {Function} handleSearch - The function to call when a change occurs.
 */
export function subscribeToEscoltas(searchQuery, handleSearch) {
  pb.collection('escolta').subscribe('*', async (e) => {
    console.log('Evento en tiempo real recibido:', e);
    await handleSearch(1); // Refresh the list with the first page
  });
}

/**
 * Unsubscribe from real-time changes in the 'escolta' collection.
 */
export function unsubscribeFromEscoltas() {
  pb.collection('escolta').unsubscribe('*');
}

/**
 * Search for escoltas based on a query.
 * @param {string} query - The search query.
 * @param {number} page - The page number.
 * @param {number} perPage - The number of items per page.
 * @returns {Object} - An object containing the escoltas, total records, and current page.
 */
export async function searchEscoltas(query, page = 1, perPage = 10) {
  const response = await pb.collection('escolta').getList(page, perPage, {
    filter: `nombre ~ "${query}"`,
  });
  return {
    escolta: response.items,
    totalRecords: response.totalItems,
    currentPage: response.currentPage,
  };
}